import React, { useState, useEffect, useCallback } from 'react';
import authService from '../../services/authService';
import AlertChannels from './AlertChannels';
import Toast from '../Toast';

const AlertingSystem = React.memo(({ orgId, teamId }) => {
  const [activeView, setActiveView] = useState('rules');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [confirmToast, setConfirmToast] = useState({ show: false, message: '', onConfirm: null, onCancel: null });
  
  // Determine if we're in team mode
  const isTeamMode = !!teamId && !orgId;
  const entityId = isTeamMode ? teamId : orgId;
  
  // Data states for different views
  const [rules, setRules] = useState([]);
  const [activeAlerts, setActiveAlerts] = useState([]);
  const [alertHistory, setAlertHistory] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [templates, setTemplates] = useState([]);
  
  // Filters and pagination
  const [filters, setFilters] = useState({
    severityFilter: '',
    statusFilter: '',
    searchTerm: '',
    sortBy: 'createdAt_desc'
  });
  const [historyFilters, setHistoryFilters] = useState({
    startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    severities: [],
    statuses: [],
    ruleNameSearch: '',
    page: 1,
    pageSize: 50
  });

  const views = React.useMemo(() => ({
    rules: { label: 'Alert Rules', apiPath: 'alertrulesapi' },
    monitoring: { label: 'Active Alerts', apiPath: 'alerthistoryapi' },
    history: { label: 'Alert History', apiPath: 'alerthistoryapi' },
    analytics: { label: 'Analytics', apiPath: 'alerthistoryapi' },
    channels: { label: 'Notification Channels', apiPath: 'alertchannelsapi' },
    templates: { label: 'Templates', apiPath: 'alerttemplatesapi' }
  }), []);

  // API helper function
  const apiCall = useCallback(async (endpoint, options = {}) => {
    const token = authService.getToken();
    const headers = {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers
    };

    try {
      const response = await fetch(endpoint, {
        ...options,
        headers,
        credentials: 'include'
      });

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch {
          // If JSON parsing fails, use status text
        }
        throw new Error(errorMessage);
      }

      // Handle 204 No Content (common for DELETE operations)
      if (response.status === 204 || response.status === 201) {
        return { success: true };
      }

      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      } else {
        // For empty responses without content-type, return success
        const text = await response.text();
        if (text.length === 0) {
          return { success: true };
        }
        throw new Error(`Unexpected response type: ${contentType}. Response: ${text.substring(0, 100)}`);
      }
    } catch (err) {
      console.error('API call error:', err);
      console.error('Endpoint:', endpoint);
      if (err instanceof TypeError && err.message.includes('fetch')) {
        throw new Error('Network error: Unable to connect to server. Please check your connection.');
      }
      throw err;
    }
  }, []);

  // Load alert rules
  const loadRules = useCallback(async () => {
    if (!entityId) return;

    setLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams({
        ...(filters.severityFilter && { severityFilter: filters.severityFilter }),
        ...(filters.statusFilter && { statusFilter: filters.statusFilter }),
        ...(filters.searchTerm && { searchTerm: filters.searchTerm }),
        ...(filters.sortBy && { sortBy: filters.sortBy })
      });
      
      const endpoint = isTeamMode 
        ? `/api/alertrulesapi/team/${teamId}?${queryParams}`
        : `/api/alertrulesapi/organization/${orgId}?${queryParams}`;
      console.log('Loading alert rules from:', endpoint);
      
      const data = await apiCall(endpoint);
      console.log('Received alert rules data:', data);
      
      // Ensure data is an array
      if (Array.isArray(data)) {
        setRules(data);
      } else if (data && Array.isArray(data.rules)) {
        setRules(data.rules);
      } else {
        console.warn('Unexpected data format:', data);
        setRules([]);
      }
    } catch (err) {
      const errorMsg = err.message || 'Unknown error occurred';
      setError(errorMsg);
      console.error('Error loading rules:', err);
      console.error('Error details:', {
        message: err.message,
        stack: err.stack,
        name: err.name
      });
    } finally {
          setLoading(false);
        }
  }, [entityId, isTeamMode, teamId, orgId, filters, apiCall]);

  // Load active alerts
  const loadActiveAlerts = useCallback(async () => {
    if (!entityId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const queryParams = new URLSearchParams({
        ...(filters.severityFilter && { severityFilter: filters.severityFilter }),
        ...(filters.statusFilter && { statusFilter: filters.statusFilter })
      });
      
      const endpoint = isTeamMode
        ? `/api/alerthistoryapi/team/${teamId}/active?${queryParams}`
        : `/api/alerthistoryapi/organization/${orgId}/active?${queryParams}`;
      const data = await apiCall(endpoint);
      setActiveAlerts(data.alerts || []);
    } catch (err) {
      setError(err.message);
      console.error('Error loading active alerts:', err);
    } finally {
          setLoading(false);
        }
  }, [entityId, isTeamMode, teamId, orgId, filters, apiCall]);

  // Load alert history
  const loadAlertHistory = useCallback(async () => {
    if (!entityId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const queryParams = new URLSearchParams({
        ...(historyFilters.startDate && { startDate: historyFilters.startDate }),
        ...(historyFilters.endDate && { endDate: historyFilters.endDate }),
        ...(historyFilters.ruleNameSearch && { ruleNameSearch: historyFilters.ruleNameSearch }),
        page: historyFilters.page.toString(),
        pageSize: historyFilters.pageSize.toString()
      });
      
      if (historyFilters.severities && historyFilters.severities.length > 0) {
        historyFilters.severities.forEach(s => queryParams.append('severities', s));
      }
      if (historyFilters.statuses && historyFilters.statuses.length > 0) {
        historyFilters.statuses.forEach(s => queryParams.append('statuses', s));
      }
      
      const endpoint = isTeamMode
        ? `/api/alerthistoryapi/team/${teamId}?${queryParams}`
        : `/api/alerthistoryapi/organization/${orgId}?${queryParams}`;
      const data = await apiCall(endpoint);
      setAlertHistory(data);
    } catch (err) {
        setError(err.message);
      console.error('Error loading alert history:', err);
    } finally {
        setLoading(false);
    }
  }, [entityId, isTeamMode, teamId, orgId, historyFilters, apiCall]);

  // Load analytics
  const loadAnalytics = useCallback(async () => {
    if (!entityId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const startDate = historyFilters.startDate || new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      const endDate = historyFilters.endDate || new Date().toISOString().split('T')[0];
      
      const endpoint = isTeamMode
        ? `/api/alerthistoryapi/team/${teamId}/analytics?startDate=${startDate}&endDate=${endDate}`
        : `/api/alerthistoryapi/organization/${orgId}/analytics?startDate=${startDate}&endDate=${endDate}`;
      const data = await apiCall(endpoint);
      setAnalytics(data);
    } catch (err) {
      setError(err.message);
      console.error('Error loading analytics:', err);
    } finally {
      setLoading(false);
    }
  }, [entityId, isTeamMode, teamId, orgId, historyFilters, apiCall]);

  // Load templates
  const loadTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await apiCall('/api/alerttemplatesapi');
      setTemplates(data);
    } catch (err) {
      setError(err.message);
      console.error('Error loading templates:', err);
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  // Load data based on active view
  useEffect(() => {
    if (!entityId) return;
    
    switch (activeView) {
      case 'rules':
        loadRules();
        break;
      case 'monitoring':
        loadActiveAlerts();
        break;
      case 'history':
        loadAlertHistory();
        break;
      case 'analytics':
        loadAnalytics();
        break;
      case 'templates':
        loadTemplates();
        break;
      default:
        break;
    }
  }, [activeView, entityId, loadRules, loadActiveAlerts, loadAlertHistory, loadAnalytics, loadTemplates]);

  // Handle tab click
  const handleTabClick = useCallback((viewKey) => {
    setActiveView(viewKey);
    setError(null);
  }, []);

  // Toggle rule active status
  const toggleRule = useCallback(async (ruleId) => {
    try {
      const endpoint = isTeamMode
        ? `/api/alertrulesapi/team/${teamId}/${ruleId}/toggle`
        : `/api/alertrulesapi/${ruleId}/toggle`;
      await apiCall(endpoint, { method: 'POST' });
      loadRules();
      } catch (err) {
      setError(err.message);
    }
  }, [apiCall, loadRules, isTeamMode, teamId]);

  // Delete rule
  const deleteRule = useCallback((ruleId) => {
    setConfirmToast({
      show: true,
      message: 'Are you sure you want to delete this alert rule?',
      onConfirm: async () => {
        setConfirmToast({ show: false, message: '', onConfirm: null, onCancel: null });
        try {
          const endpoint = isTeamMode
            ? `/api/alertrulesapi/team/${teamId}/${ruleId}`
            : `/api/alertrulesapi/${ruleId}`;
          await apiCall(endpoint, { method: 'DELETE' });
          loadRules();
          setToast({ show: true, message: 'Alert rule deleted successfully', type: 'success' });
        } catch (err) {
          setToast({ show: true, message: err.message || 'Failed to delete alert rule', type: 'error' });
        }
      },
      onCancel: () => {
        setConfirmToast({ show: false, message: '', onConfirm: null, onCancel: null });
      }
    });
  }, [apiCall, loadRules, isTeamMode, teamId]);

  // Test rule
  const testRule = useCallback(async (ruleId) => {
    try {
      setLoading(true);
      setError(null);
      const endpoint = isTeamMode
        ? `/api/alertrulesapi/team/${teamId}/${ruleId}/test`
        : `/api/alertrulesapi/${ruleId}/test`;
      const result = await apiCall(endpoint, { method: 'POST' });
      
      // Build a more informative message
      let message = result.message || (result.wouldTrigger ? 'This rule would trigger with current metrics' : 'This rule would not trigger with current metrics');
      if (result.wouldTrigger && result.notificationsSent !== undefined) {
        if (result.notificationsSent > 0) {
          message += ` Test notifications sent to ${result.notificationsSent} channel(s).`;
        } else {
          message += ' No active notification channels configured for this rule.';
        }
      }
      
      setToast({ 
        show: true, 
        message: message, 
        type: result.wouldTrigger ? 'success' : 'info' 
      });
      
      // Reload rules to update LastTriggeredAt if the rule triggered
      if (result.wouldTrigger) {
        loadRules();
      }
    } catch (err) {
      setError(err.message || 'Failed to test rule');
    } finally {
      setLoading(false);
    }
  }, [apiCall, loadRules, isTeamMode, teamId]);

  // Acknowledge alert
  const acknowledgeAlert = useCallback(async (alertId, note) => {
    try {
      await apiCall(`/api/alerthistoryapi/${alertId}/acknowledge`, {
        method: 'POST',
        body: JSON.stringify({ note })
      });
      loadActiveAlerts();
    } catch (err) {
      setError(err.message);
    }
  }, [apiCall, loadActiveAlerts]);

  // Resolve alert
  const resolveAlert = useCallback(async (alertId, note) => {
    try {
      await apiCall(`/api/alerthistoryapi/${alertId}/resolve`, {
        method: 'POST',
        body: JSON.stringify({ note })
      });
      loadActiveAlerts();
    } catch (err) {
      setError(err.message);
    }
  }, [apiCall, loadActiveAlerts]);

  // Create rule from template
  const handleUseTemplate = useCallback(async (template) => {
    if (!entityId) {
      setError(isTeamMode ? 'Team ID is required' : 'Organization ID is required');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);

      // Get template details with parsed conditions
      const templateData = await apiCall(`/api/alerttemplatesapi/${template.id}`);
      
      // Get available channels for the organization/team
      const channelsEndpoint = isTeamMode
        ? `/api/alertchannelsapi/team/${teamId}`
        : `/api/alertchannelsapi/organization/${orgId}`;
      const channelsData = await apiCall(channelsEndpoint);
      const availableChannels = channelsData.channels || [];

      if (availableChannels.length === 0) {
        setError('No notification channels configured. Please create a channel first.');
        setLoading(false);
        return;
      }

      // Create rule from template
      const ruleData = isTeamMode ? {
        teamId: teamId,
        name: `${template.templateName} - ${new Date().toLocaleDateString()}`,
        description: template.description || `Alert rule created from template: ${template.templateName}`,
        isActive: true,
        severity: 1, // Warning as default
        triggerType: 0, // Threshold as default
        evaluationWindowMinutes: 60,
        cooldownMinutes: 15,
        conditions: templateData.defaultConditions || [],
        selectedChannelIds: availableChannels
          .filter(c => c.isActive)
          .slice(0, 1) // Use first active channel
          .map(c => c.id),
        templateId: template.id
      } : {
        organizationId: orgId,
        name: `${template.templateName} - ${new Date().toLocaleDateString()}`,
        description: template.description || `Alert rule created from template: ${template.templateName}`,
        isActive: true,
        severity: 1, // Warning as default
        triggerType: 0, // Threshold as default
        evaluationWindowMinutes: 60,
        cooldownMinutes: 15,
        conditions: templateData.defaultConditions || [],
        selectedChannelIds: availableChannels
          .filter(c => c.isActive)
          .slice(0, 1) // Use first active channel
          .map(c => c.id),
        templateId: template.id
      };

      const endpoint = isTeamMode ? '/api/alertrulesapi/team' : '/api/alertrulesapi';
      const createdRule = await apiCall(endpoint, {
        method: 'POST',
        body: JSON.stringify(ruleData)
      });

      // Switch to rules view and reload
      setActiveView('rules');
      await loadRules();
      
      // Show success message
      setError(null);
      setToast({ 
        show: true, 
        message: `Alert rule "${createdRule.name}" created successfully from template!`, 
        type: 'success' 
      });
    } catch (err) {
      setError(err.message || 'Failed to create rule from template');
      console.error('Error creating rule from template:', err);
    } finally {
      setLoading(false);
    }
  }, [orgId, teamId, isTeamMode, entityId, apiCall, loadRules]);

  if (!entityId) {
    return (
      <div style={{ padding: '24px', textAlign: 'center', color: '#6b7280' }}>
        Please select an {isTeamMode ? 'team' : 'organization'} to view alerting settings.
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Navigation Tabs */}
      <div 
        style={{ 
          display: 'flex', 
          gap: 0, 
          marginBottom: 16, 
          borderBottom: '1px solid #e5e7eb',
          overflowX: 'auto'
        }}
      >
        {Object.entries(views).map(([key, view]) => (
          <button
            key={key}
            type="button"
            className="alert-tab"
            style={{
              background: activeView === key ? '#fff' : 'transparent',
              color: activeView === key ? '#111827' : '#6b7280',
              border: 'none',
              borderBottom: activeView === key ? '2px solid #111827' : '2px solid transparent',
              padding: '10px 20px',
              cursor: 'pointer',
              fontWeight: activeView === key ? 600 : 400,
              fontSize: '14px',
              whiteSpace: 'nowrap',
              minWidth: 'fit-content'
            }}
            onClick={() => handleTabClick(key)}
          >
            {view.label}
          </button>
        ))}
      </div>

      {/* Error Display */}
      {error && (
        <div style={{ 
          padding: '12px 16px', 
          marginBottom: '16px', 
          backgroundColor: '#fee2e2', 
          color: '#991b1b', 
          borderRadius: '6px',
          border: '1px solid #fecaca'
        }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Content Area */}
      <div 
        style={{ 
          flex: 1, 
          border: '1px solid #e5e7eb', 
          borderRadius: '8px', 
          overflow: 'auto',
          background: '#fff',
          minHeight: '600px',
          position: 'relative',
          padding: '24px'
        }}
      >
        {loading && (
          <div style={{ 
            position: 'absolute', 
            top: '50%', 
            left: '50%', 
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            color: '#6b7280'
          }}>
            <div style={{ marginBottom: '12px' }}>Loading...</div>
            <div className="spinner-border spinner-border-sm" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}

        {!loading && (
          <>
            {activeView === 'rules' && (
              <RulesView 
                rules={rules} 
                filters={filters}
                setFilters={setFilters}
                onToggle={toggleRule}
                onDelete={deleteRule}
                onTest={testRule}
              />
            )}
            
            {activeView === 'channels' && (
              <AlertChannels orgId={orgId} teamId={teamId} />
            )}
            
            {activeView === 'monitoring' && (
              <ActiveAlertsView 
                alerts={activeAlerts}
                onAcknowledge={acknowledgeAlert}
                onResolve={resolveAlert}
              />
            )}
            
            {activeView === 'history' && (
              <HistoryView 
                history={alertHistory}
                filters={historyFilters}
                setFilters={setHistoryFilters}
              />
            )}
            
            {activeView === 'analytics' && (
              <AnalyticsView analytics={analytics} />
            )}
            
            {activeView === 'templates' && (
              <TemplatesView 
                templates={templates} 
                orgId={orgId}
                onUseTemplate={handleUseTemplate}
              />
            )}
          </>
        )}
      </div>

      {/* Toast Components */}
      <Toast 
        message={toast.message} 
        type={toast.type} 
        show={toast.show} 
        onClose={() => setToast({ ...toast, show: false })} 
      />
      <Toast 
        message={confirmToast.message} 
        type="warning"
        show={confirmToast.show} 
        persist={true}
        actionLabel="Confirm"
        onAction={confirmToast.onConfirm}
        secondaryActionLabel="Cancel"
        onSecondaryAction={confirmToast.onCancel}
        onClose={() => setConfirmToast({ ...confirmToast, show: false })} 
      />
    </div>
  );
}, (prevProps, nextProps) => {
  return prevProps.orgId === nextProps.orgId;
});

// Rules View Component
const RulesView = ({ rules, filters, setFilters, onToggle, onDelete, onTest }) => {
  return (
    <div>
      <div style={{ marginBottom: '20px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Search rules..."
          value={filters.searchTerm}
          onChange={(e) => setFilters({ ...filters, searchTerm: e.target.value })}
          style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', flex: '1', minWidth: '200px' }}
        />
        <select
          value={filters.severityFilter}
          onChange={(e) => setFilters({ ...filters, severityFilter: e.target.value })}
          style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px' }}
        >
          <option value="">All Severities</option>
          <option value="Info">Info</option>
          <option value="Warning">Warning</option>
          <option value="Critical">Critical</option>
          <option value="Emergency">Emergency</option>
        </select>
        <select
          value={filters.statusFilter}
          onChange={(e) => setFilters({ ...filters, statusFilter: e.target.value })}
          style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px' }}
        >
          <option value="">All Statuses</option>
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
        </select>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Name</th>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Severity</th>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Status</th>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Trigger Count</th>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Last Triggered</th>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rules.length === 0 ? (
              <tr>
                <td colSpan="6" style={{ padding: '24px', textAlign: 'center', color: '#6b7280' }}>
                  No alert rules found.
                </td>
              </tr>
            ) : (
              rules.map((rule) => (
                <tr key={rule.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '12px' }}>{rule.name}</td>
                  <td style={{ padding: '12px' }}>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: 500,
                      backgroundColor: rule.severity === 'Critical' || rule.severity === 'Emergency' ? '#fee2e2' : 
                                     rule.severity === 'Warning' ? '#fef3c7' : '#dbeafe',
                      color: rule.severity === 'Critical' || rule.severity === 'Emergency' ? '#991b1b' : 
                            rule.severity === 'Warning' ? '#92400e' : '#1e40af'
                    }}>
                      {rule.severity}
                    </span>
                  </td>
                  <td style={{ padding: '12px' }}>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: 500,
                      backgroundColor: rule.isActive ? '#d1fae5' : '#f3f4f6',
                      color: rule.isActive ? '#065f46' : '#6b7280'
                    }}>
                      {rule.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ padding: '12px' }}>{rule.triggerCount}</td>
                  <td style={{ padding: '12px' }}>
                    {rule.lastTriggeredAt ? new Date(rule.lastTriggeredAt).toLocaleString() : 'Never'}
                  </td>
                  <td style={{ padding: '12px' }}>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <button
                        onClick={() => onToggle(rule.id)}
                        style={{ padding: '4px 8px', fontSize: '12px', border: '1px solid #d1d5db', borderRadius: '4px', backgroundColor: '#ffffff', cursor: 'pointer' }}
                      >
                        {rule.isActive ? 'Deactivate' : 'Activate'}
                      </button>
                      <button
                        onClick={() => onTest(rule.id)}
                        style={{ padding: '4px 8px', fontSize: '12px', border: '1px solid #3b82f6', borderRadius: '4px', backgroundColor: '#ffffff', color: '#3b82f6', cursor: 'pointer' }}
                      >
                        Test
                      </button>
                      <button
                        onClick={() => onDelete(rule.id)}
                        style={{ padding: '4px 8px', fontSize: '12px', border: '1px solid #dc2626', borderRadius: '4px', backgroundColor: '#ffffff', color: '#dc2626', cursor: 'pointer' }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Active Alerts View Component
const ActiveAlertsView = ({ alerts, onAcknowledge, onResolve }) => {
  return (
    <div>
      <div style={{ marginBottom: '20px' }}>
        <h3 style={{ margin: 0 }}>Active Alerts ({alerts.length})</h3>
      </div>

      <div style={{ display: 'grid', gap: '12px' }}>
        {alerts.length === 0 ? (
          <div style={{ padding: '24px', textAlign: 'center', color: '#6b7280' }}>
            No active alerts.
          </div>
        ) : (
          alerts.map((alert) => (
            <div key={alert.id} style={{ 
              border: '1px solid #e5e7eb', 
              borderRadius: '8px', 
              padding: '16px',
              borderLeft: `4px solid ${alert.severity === 'Critical' || alert.severity === 'Emergency' ? '#dc2626' : 
                                                                    alert.severity === 'Warning' ? '#f59e0b' : '#3b82f6'}`
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <div>
                  <div style={{ fontWeight: 600, marginBottom: '4px' }}>{alert.ruleName}</div>
                  <div style={{ fontSize: '14px', color: '#6b7280' }}>{alert.message}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <span style={{
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: 500,
                    backgroundColor: alert.severity === 'Critical' || alert.severity === 'Emergency' ? '#fee2e2' : 
                                   alert.severity === 'Warning' ? '#fef3c7' : '#dbeafe',
                    color: alert.severity === 'Critical' || alert.severity === 'Emergency' ? '#991b1b' : 
                          alert.severity === 'Warning' ? '#92400e' : '#1e40af',
                    marginRight: '8px'
                  }}>
                    {alert.severity}
                  </span>
                </div>
              </div>
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>
                Triggered: {new Date(alert.triggeredAt).toLocaleString()}
              </div>
              <div>
                <button
                  onClick={() => onAcknowledge(alert.id, '')}
                  style={{ marginRight: '8px', padding: '6px 12px', fontSize: '12px' }}
                >
                  Acknowledge
                </button>
                <button
                  onClick={() => onResolve(alert.id, '')}
                  style={{ padding: '6px 12px', fontSize: '12px' }}
                >
                  Resolve
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// History View Component
const HistoryView = ({ history, filters, setFilters }) => {
  return (
    <div>
      <div style={{ marginBottom: '20px' }}>
        <h3 style={{ margin: 0 }}>Alert History</h3>
        <div style={{ marginTop: '12px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <input
            type="date"
            value={filters.startDate}
            onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
            style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px' }}
          />
          <input
            type="date"
            value={filters.endDate}
            onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
            style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px' }}
          />
          <input
            type="text"
            placeholder="Search rule name..."
            value={filters.ruleNameSearch}
            onChange={(e) => setFilters({ ...filters, ruleNameSearch: e.target.value })}
            style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', flex: '1', minWidth: '200px' }}
          />
        </div>
      </div>

      {history && history.alerts && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Rule</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Severity</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Status</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Triggered</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Resolved</th>
              </tr>
            </thead>
            <tbody>
              {history.alerts.length === 0 ? (
                <tr>
                  <td colSpan="5" style={{ padding: '24px', textAlign: 'center', color: '#6b7280' }}>
                    No alert history found.
                  </td>
                </tr>
              ) : (
                history.alerts.map((alert) => (
                  <tr key={alert.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '12px' }}>{alert.ruleName}</td>
                    <td style={{ padding: '12px' }}>{alert.severity}</td>
                    <td style={{ padding: '12px' }}>{alert.status}</td>
                    <td style={{ padding: '12px' }}>{new Date(alert.triggeredAt).toLocaleString()}</td>
                    <td style={{ padding: '12px' }}>
                      {alert.resolvedAt ? new Date(alert.resolvedAt).toLocaleString() : '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Analytics View Component
const AnalyticsView = ({ analytics }) => {
  if (!analytics) {
    return <div style={{ padding: '24px', textAlign: 'center', color: '#6b7280' }}>Loading analytics...</div>;
  }

  return (
    <div>
      <h3 style={{ marginBottom: '20px' }}>Alert Analytics</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px' }}>
          <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>Total Alerts</div>
          <div style={{ fontSize: '24px', fontWeight: 600 }}>{analytics.totalAlerts}</div>
        </div>
        <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px' }}>
          <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>Active</div>
          <div style={{ fontSize: '24px', fontWeight: 600 }}>{analytics.activeAlerts}</div>
        </div>
        <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px' }}>
          <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>Resolved</div>
          <div style={{ fontSize: '24px', fontWeight: 600 }}>{analytics.resolvedAlerts}</div>
        </div>
        <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px' }}>
          <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>Acknowledged</div>
          <div style={{ fontSize: '24px', fontWeight: 600 }}>{analytics.acknowledgedAlerts}</div>
        </div>
      </div>
    </div>
  );
};

// Templates View Component
const TemplatesView = ({ templates, orgId, onUseTemplate }) => {
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  const handleUseTemplate = async (template) => {
    if (onUseTemplate) {
      await onUseTemplate(template);
    }
  };

  const handleViewDetails = (template) => {
    setSelectedTemplate(template);
    setShowDetails(true);
  };

  return (
    <div>
      <h3 style={{ marginBottom: '20px' }}>Alert Templates</h3>
      <div style={{ display: 'grid', gap: '16px', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }}>
        {templates.length === 0 ? (
          <div style={{ padding: '24px', textAlign: 'center', color: '#6b7280', gridColumn: '1 / -1' }}>
            No templates available.
          </div>
        ) : (
          templates.map((template) => (
            <div key={template.id} style={{ 
              border: '1px solid #e5e7eb', 
              borderRadius: '8px', 
              padding: '16px',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              backgroundColor: '#ffffff'
            }}>
              <div>
                <div style={{ fontWeight: 600, marginBottom: '4px', fontSize: '16px' }}>
                  {template.templateName}
                </div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>
                  <span style={{ 
                    padding: '2px 8px', 
                    borderRadius: '4px', 
                    backgroundColor: '#f3f4f6',
                    marginRight: '8px'
                  }}>
                    {template.category}
                  </span>
                  {template.minimumTier && (
                    <span style={{ 
                      padding: '2px 8px', 
                      borderRadius: '4px', 
                      backgroundColor: '#dbeafe',
                      color: '#1e40af'
                    }}>
                      {template.minimumTier}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: '14px', color: '#4b5563', marginBottom: '12px' }}>
                  {template.description || 'No description available.'}
                </div>
              </div>
              
              {template.defaultConditions && template.defaultConditions.length > 0 && (
                <div style={{ fontSize: '12px', color: '#6b7280' }}>
                  <strong>Conditions:</strong> {template.defaultConditions.length} condition(s)
                </div>
              )}

              <div style={{ display: 'flex', gap: '8px', marginTop: 'auto' }}>
                <button
                  onClick={() => handleViewDetails(template)}
                  style={{
                    padding: '6px 12px',
                    fontSize: '14px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    backgroundColor: '#ffffff',
                    color: '#374151',
                    cursor: 'pointer',
                    flex: 1
                  }}
                  onMouseOver={(e) => e.target.style.backgroundColor = '#f9fafb'}
                  onMouseOut={(e) => e.target.style.backgroundColor = '#ffffff'}
                >
                  View Details
                </button>
                <button
                  onClick={() => handleUseTemplate(template)}
                  style={{
                    padding: '6px 12px',
                    fontSize: '14px',
                    border: 'none',
                    borderRadius: '6px',
                    backgroundColor: '#3b82f6',
                    color: '#ffffff',
                    cursor: 'pointer',
                    fontWeight: 500,
                    flex: 1
                  }}
                  onMouseOver={(e) => e.target.style.backgroundColor = '#2563eb'}
                  onMouseOut={(e) => e.target.style.backgroundColor = '#3b82f6'}
                >
                  Use Template
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {showDetails && selectedTemplate && (
        <TemplateDetailsModal
          template={selectedTemplate}
          onClose={() => {
            setShowDetails(false);
            setSelectedTemplate(null);
          }}
          onUseTemplate={() => {
            handleUseTemplate(selectedTemplate);
            setShowDetails(false);
            setSelectedTemplate(null);
          }}
        />
      )}
    </div>
  );
};

const TemplateDetailsModal = ({ template, onClose, onUseTemplate }) => {
  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }} onClick={onClose}>
      <div style={{
        backgroundColor: '#ffffff',
        borderRadius: '8px',
        padding: '24px',
        maxWidth: '600px',
        width: '90%',
        maxHeight: '80vh',
        overflow: 'auto',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
      }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '20px' }}>
          <div>
            <h3 style={{ margin: 0, marginBottom: '8px' }}>{template.templateName}</h3>
            <div style={{ fontSize: '14px', color: '#6b7280' }}>
              <span style={{ 
                padding: '2px 8px', 
                borderRadius: '4px', 
                backgroundColor: '#f3f4f6',
                marginRight: '8px'
              }}>
                {template.category}
              </span>
              {template.minimumTier && (
                <span style={{ 
                  padding: '2px 8px', 
                  borderRadius: '4px', 
                  backgroundColor: '#dbeafe',
                  color: '#1e40af'
                }}>
                  {template.minimumTier}
                </span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              border: 'none',
              background: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#6b7280',
              padding: '0',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ×
          </button>
        </div>

        {template.description && (
          <div style={{ marginBottom: '20px', color: '#4b5563' }}>
            {template.description}
          </div>
        )}

        {template.defaultConditions && template.defaultConditions.length > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <h4 style={{ marginBottom: '12px', fontSize: '16px', fontWeight: 600 }}>Conditions</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {template.defaultConditions.map((condition, idx) => (
                <div key={idx} style={{
                  padding: '12px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  backgroundColor: '#f9fafb'
                }}>
                  <div style={{ fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>
                    {condition.metricPath}
                  </div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>
                    {condition.operator} {condition.thresholdValue} {condition.thresholdUnit || ''}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '24px' }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 16px',
              fontSize: '14px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              backgroundColor: '#ffffff',
              color: '#374151',
              cursor: 'pointer'
            }}
          >
            Close
          </button>
          <button
            onClick={onUseTemplate}
            style={{
              padding: '8px 16px',
              fontSize: '14px',
              border: 'none',
              borderRadius: '6px',
              backgroundColor: '#3b82f6',
              color: '#ffffff',
              cursor: 'pointer',
              fontWeight: 500
            }}
          >
            Use This Template
          </button>
        </div>
      </div>
    </div>
  );
};

export default AlertingSystem;
